import axios from "axios";
import { CRITICAL, openCritical } from "@/services/critical";
import realtime from "@/services/realtime";

/**
 * REST-клиент bunker (CONTRACT §2). Все функции возвращают ТЕЛО ответа (response.data).
 *
 * Ошибки нормализуются в ApiError { status, code, message, serverMessage, data }:
 *  - status: HTTP-код (0 — сеть/таймаут);
 *  - code: код сервера ({code} из JSON) либо 'NETWORK' | 'TIMEOUT' | 'HTTP_<status>';
 *  - message: человекочитаемый русский текст (можно сразу показывать в toast).
 *
 * 401 на запросах с токеном → WebSocket закрывается, критический экран «Сессия истекла» (один раз).
 */

export class ApiError extends Error {
  constructor({ status = 0, code = "UNKNOWN", message = "Ошибка запроса", serverMessage = null, data = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
    this.data = data;
  }
}

/** Русские тексты для известных кодов сервера. */
const CODE_MESSAGES = {
  INVALID_NAME: "Имя: 2–24 символа — буквы, цифры, пробел, «_», «-», «.»",
  INVALID_AVATAR: "Подойдёт только картинка JPG или PNG",
  FILE_TOO_LARGE: "Файл слишком большой",
  AVATAR_UPLOAD_FAILED: "Не удалось сохранить фото. Попробуйте позже.",
  NOT_CONNECTED: "Нет соединения с игровым сервером. Попробуйте ещё раз.",
  UNAUTHORIZED: "Сессия истекла",
  NOT_FOUND: "Не найдено",
};

function defaultMessage(status, code) {
  if (CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  if (code === "TIMEOUT") return "Сервер не отвечает. Попробуйте ещё раз.";
  if (code === "NETWORK") return "Нет соединения с сервером";
  if (status === 400) return "Некорректный запрос";
  if (status === 401) return "Сессия истекла";
  if (status === 403) return "Доступ запрещён";
  if (status === 404) return "Не найдено";
  if (status === 409) return "Конфликт данных. Попробуйте ещё раз.";
  if (status === 413) return "Файл слишком большой";
  if (status >= 500) return "Ошибка сервера. Попробуйте позже.";
  return "Ошибка запроса";
}

/** Преобразует ошибку axios в ApiError. */
export function normalizeError(error) {
  if (error instanceof ApiError) return error;
  if (axios.isCancel && axios.isCancel(error)) {
    return new ApiError({ status: 0, code: "CANCELLED", message: "Запрос отменён" });
  }
  const response = error && error.response;
  if (!response) {
    const isTimeout = error && (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT");
    const code = isTimeout ? "TIMEOUT" : "NETWORK";
    return new ApiError({ status: 0, code, message: defaultMessage(0, code) });
  }
  const status = response.status;
  const data = response.data;
  let code = `HTTP_${status}`;
  let serverMessage = null;
  if (data && typeof data === "object") {
    if (typeof data.code === "string" && data.code) code = data.code;
    if (typeof data.message === "string") serverMessage = data.message;
  } else if (typeof data === "string" && data.trim()) {
    serverMessage = data.trim();
  }
  return new ApiError({ status, code, message: defaultMessage(status, code), serverMessage, data });
}

let tokenProvider = () => null;
let sessionExpiredHandled = false;

/** Источник JWT (регистрируется в main.js: () => useSessionStore().token). */
export function setTokenProvider(fn) {
  tokenProvider = typeof fn === "function" ? fn : () => null;
  sessionExpiredHandled = false;
}

/** Единственный axios-инстанс приложения (mockApi подменяет ему adapter). */
export const http = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

http.interceptors.request.use((config) => {
  if (!config.skipAuth) {
    const token = tokenProvider();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const apiError = normalizeError(error);
    const config = (error && error.config) || {};
    if (apiError.status === 401 && !config.skipAuth && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      // Токен недействителен и для WebSocket: прекращаем переподключения, иначе по истечении
      // их бюджета экран «Сессия истекла» сменился бы на «Нет соединения с сервером».
      realtime.close();
      openCritical(CRITICAL.SESSION_EXPIRED);
    }
    if (process.env.NODE_ENV === "development") {
      console.warn(`[api] ${config.method || ""} ${config.url || ""} →`, apiError.status, apiError.code);
    }
    return Promise.reject(apiError);
  },
);

const body = (promise) => promise.then((response) => response.data);
const get = (url, config) => body(http.get(url, config));
const post = (url, data, config) => body(http.post(url, data, config));

/* ------------------------------------------------------------------ auth */
// Эндпоинты авторизации возвращают «сырой» JWT строкой.

/** POST /auth/telegram {telegramToken: initData} → JWT (string). */
export const telegramAuth = (initData) => post("/auth/telegram", { telegramToken: initData }, { skipAuth: true });

/** POST /auth/credentials {login, password} → JWT (string). */
export const credentialsAuth = (login, password) => post("/auth/credentials", { login, password }, { skipAuth: true });

/** POST /auth/register {login, password} → JWT (string). 409 — логин занят. */
export const registerUser = (login, password) => post("/auth/register", { login, password }, { skipAuth: true });

/* ------------------------------------------------------------------ game */

/** GET текущий игрок → PlayerInfo. */
export const getMe = () => get("/api/game/get-current-player-info");

/** POST соперник по игре → PlayerInfo (404 если соперника нет). */
export const getOpponentInfo = (gameUUID) => post("/api/game/get-opponent-info", { gameUUID });

/** GET создать/присоединиться к игре → {uuid, role:'INITIATOR'|'NOT_INITIATOR', gameMode}; 409 NOT_CONNECTED без WS. */
export const createGame = (mode = "BLINK") => get("/api/game/create-game", { params: { mode } });

/** POST отменить поиск → {ok:true}. */
export const cancelSearch = (gameUUID) => post("/api/game/cancel-search", { gameUUID });

/** POST статус игры → {status, winnerName, looserName, gameMode} (status null — игра неизвестна). */
export const checkGameStatus = (gameUUID) => post("/api/game/check-game-status", { gameUUID });

/** POST результат игры → GameResult ({status:'FINAL', winnerName, looserName, winnerUserId, …}) или {status}. */
export const getGameResult = (gameUUID) => post("/api/game/get-game-result", { gameUUID });

/** POST ссылка на фото проигравшего → {looserImgLink} (может быть null). */
export const getLooserImgLink = (gameUUID) => post("/api/game/get-looser-img-link", { gameUUID });

/** GET рейтинг → RatingRow[] (limit по умолчанию 50, максимум 100). */
export const getRating = (limit = 50, offset = 0) => get("/api/game/players-rating", { params: { limit, offset } });

/** GET моя строка рейтинга → RatingRow (rank null, если ещё не играл). */
export const getMyRating = () => get("/api/game/my-rating");

/** GET игроки в сети (открыт WebSocket) → {playersOnline}. */
export const getPlayersOnline = () => get("/api/game/players-online");

/** POST сменить имя → PlayerInfo; 400 {code:'INVALID_NAME'}. */
export const updatePlayerName = (name) => post("/api/game/update-player-name", { name });

/**
 * POST multipart (часть file, JPEG/PNG) → PlayerInfo;
 * 400 INVALID_AVATAR, 413 FILE_TOO_LARGE, 503 AVATAR_UPLOAD_FAILED.
 */
export const updatePlayerAvatar = (blob) => {
  const form = new FormData();
  form.append("file", blob, "avatar.jpg");
  // Не JSON: иначе axios превратит FormData в JSON по Content-Type инстанса. Boundary допишет браузер.
  return post("/api/game/update-player-avatar", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
};

/** POST убрать загруженный аватар → PlayerInfo (снова фото из Telegram). */
export const resetPlayerAvatar = () => post("/api/game/reset-player-avatar");

/** Собственная позиция и настоящие соседи в порядке серверного рейтинга. */
export const getRatingContext = (radius = 1) => get("/api/game/my-rating-context", { params: { radius } });

/** Одна общая запись подтверждённого нарушения; только участники игры. */
export const getPostRoundReplay = (gameUUID, signal) => post("/api/game/post-round-replay", { gameUUID }, { signal, timeout: 2800 });
/** Private duels: one request id is retained across retries. */
export const createRoom = (mode, requestId) => post("/api/rooms/create", { mode, requestId });
export const joinRoom = (inviteToken, requestId) => post("/api/rooms/join", { inviteToken, requestId });
export const getCurrentRoom = () => get("/api/rooms/current");
export const getRoomState = (roomId) => post("/api/rooms/state", { roomId });
export const leaveRoom = (roomId) => post("/api/rooms/leave", { roomId });

/** Host only, while waiting: changing mode keeps the same invitation and expiry. */
export const setRoomMode = (roomId, mode) => post("/api/rooms/mode", { roomId, mode });
